import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Crown, Loader2, Save, Store, Search, AlertTriangle, ShieldCheck, Pencil, X } from "lucide-react";
import { api, apiError, fmt } from "@/lib/api";

const FIELDS = [
  { key: "price", label: "Membership price (₹, one-time)", step: "1", min: 1 },
  { key: "validity_days", label: "Validity (days)", step: "1", min: 1 },
  { key: "min_order", label: "Min. merchandise total for benefit (₹)", step: "1", min: 0 },
  { key: "delivery_discount", label: "Delivery discount per order (₹)", step: "1", min: 1 },
  { key: "total_cap", label: "Total savings cap per membership (₹)", step: "1", min: 1 },
  { key: "max_deliveries", label: "Max discounted deliveries per membership", step: "1", min: 1 },
  { key: "max_discount_pct", label: "Margin floor — max PRIME discount (%)", step: "1", min: 0 },
  { key: "radius_km", label: "Outlet delivery radius (km)", step: "0.5", min: 1 },
];

const inputCls = "h-10 w-full rounded-lg border border-white/10 bg-black/30 px-3 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";

export default function AdminPrime() {
  const [cfg, setCfg] = useState(null);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: c }, { data: m }] = await Promise.all([
        api.get("/admin/prime/config"),
        api.get("/admin/prime/members"),
      ]);
      setCfg(c);
      setMembers(m.members || []);
    } catch (err) {
      toast.error(apiError(err, "Could not load PRIME settings"));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      FIELDS.forEach((f) => { payload[f.key] = parseFloat(cfg[f.key]); });
      const { data } = await api.put("/admin/prime/config", payload);
      setCfg(data);
      toast.success("PRIME settings saved");
    } catch (err) {
      toast.error(apiError(err, "Could not save settings"));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;

  return (
    <div className="mx-auto max-w-5xl" data-testid="admin-prime-page">
      <div className="flex items-center gap-3">
        <Crown className="h-6 w-6 text-amber-500" />
        <h1 className="font-display text-2xl font-bold text-white">PAQVERO PRIME</h1>
      </div>
      <p className="mt-1 text-sm text-neutral-400">Membership plan settings and member list. Price applies to new purchases; changes never touch active memberships.</p>

      <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
        <h2 className="text-sm font-extrabold uppercase tracking-[0.14em] text-neutral-300">Plan settings</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="mb-1.5 block text-xs font-semibold text-neutral-400">{f.label}</label>
              <input
                type="number" step={f.step} min={f.min}
                value={cfg[f.key]}
                onChange={(e) => setCfg((c) => ({ ...c, [f.key]: e.target.value }))}
                className={inputCls}
                data-testid={`prime-config-${f.key}`}
              />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-xs font-semibold text-neutral-400">Outlet label</label>
            <input
              value={cfg.outlet_label}
              onChange={(e) => setCfg((c) => ({ ...c, outlet_label: e.target.value }))}
              className={inputCls}
              data-testid="prime-config-outlet"
            />
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="prime-config-save"
          className="mt-5 flex items-center gap-2 rounded-xl bg-amber-500 px-6 py-2.5 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save settings
        </button>
      </div>

      <PrimeStoreManager />

      <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
        <h2 className="text-sm font-extrabold uppercase tracking-[0.14em] text-neutral-300">Members ({members.length})</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="prime-members-table">
            <thead>
              <tr className="border-b border-white/10 text-[11px] uppercase tracking-wider text-neutral-500">
                <th className="pb-2 pr-4">Customer</th>
                <th className="pb-2 pr-4">Payment</th>
                <th className="pb-2 pr-4">Valid till</th>
                <th className="pb-2 pr-4">Savings used</th>
                <th className="pb-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-xs text-neutral-600" data-testid="prime-members-empty">No PRIME purchases yet.</td></tr>
              )}
              {members.map((m) => (
                <tr key={m.id} className="border-b border-white/5" data-testid={`prime-member-${m.id}`}>
                  <td className="py-2.5 pr-4">
                    <p className="font-semibold text-white">{m.user_name || "—"}</p>
                    <p className="text-xs text-neutral-500">{m.user_email}</p>
                  </td>
                  <td className="py-2.5 pr-4 text-xs text-neutral-400">
                    {m.amount ? fmt(m.amount) : "—"}
                    {m.razorpay_payment_id && <p className="text-[10px] text-neutral-600">{m.razorpay_payment_id}</p>}
                  </td>
                  <td className="py-2.5 pr-4 text-xs text-neutral-400">
                    {m.expires_at ? new Date(m.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                  </td>
                  <td className="py-2.5 pr-4 text-xs text-neutral-400">
                    {fmt(m.allowance_used || 0)} / {fmt(m.cap_total || 0)} · {m.uses_count} orders
                  </td>
                  <td className="py-2.5">
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase ${
                      m.status === "active" ? "bg-emerald-500/15 text-emerald-400"
                      : m.status === "pending_payment" ? "bg-amber-500/15 text-amber-400"
                      : m.status === "failed" ? "bg-red-500/15 text-red-400"
                      : "bg-white/10 text-neutral-400"
                    }`}>{m.status.replace("_", " ")}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PRIME Store product management: selection, member pricing, margin guardrails
// ---------------------------------------------------------------------------
function PrimeStoreManager() {
  const [rows, setRows] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [stockF, setStockF] = useState("");
  const [primeF, setPrimeF] = useState("");
  const [drafts, setDrafts] = useState({});   // id -> {prime_store, prime_price, authorize, variant_prices: {sku: str}}
  const [checked, setChecked] = useState({}); // id -> bool (bulk selection)
  const [varModal, setVarModal] = useState(null); // row whose per-variant editor is open
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (category) params.set("category", category);
      if (brand) params.set("brand_id", brand);
      if (stockF) params.set("status", stockF);
      if (primeF) params.set("prime", primeF);
      const { data } = await api.get(`/admin/prime/store/products?${params}`);
      setRows(data.products || []);
    } catch (err) {
      toast.error(apiError(err, "Could not load products"));
    }
  }, [q, category, brand, stockF, primeF]);

  useEffect(() => {
    api.get("/shop/categories").then(({ data }) => setCategories(data.categories || data || [])).catch(() => {});
    api.get("/shop/brands").then(({ data }) => setBrands(data.brands || data || [])).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const serverOverrides = (r) => Object.fromEntries((r.variants || []).filter((v) => v.override != null).map((v) => [v.sku, String(v.override)]));
  const draft = (r) => drafts[r.id] || { prime_store: r.prime_store, prime_price: r.prime_price ?? "", authorize: false, variant_prices: serverOverrides(r) };
  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));
  const setVariantPrice = (r, sku, value) => setDrafts((d) => ({ ...d, [r.id]: { ...(d[r.id] || draft(r)), variant_prices: { ...((d[r.id] || draft(r)).variant_prices || {}), [sku]: value } } }));
  const parseVariantPrices = (d) => Object.fromEntries(Object.entries(d.variant_prices || {}).map(([k, v]) => [k, v === "" ? 0 : parseFloat(v)]));
  const isDirty = (r) => {
    const d = drafts[r.id];
    if (!d) return false;
    const baseDirty = d.prime_store !== r.prime_store || (d.prime_price !== "" && parseFloat(d.prime_price) !== r.prime_price) || (d.prime_store && d.prime_price === "" && !r.prime_price);
    const so = serverOverrides(r);
    const dv = d.variant_prices || {};
    const keys = new Set([...Object.keys(so), ...Object.keys(dv)]);
    const varDirty = [...keys].some((k) => (dv[k] ?? so[k] ?? "") !== (so[k] ?? ""));
    return baseDirty || varDirty;
  };
  const dirtyRows = (rows || []).filter(isDirty);
  const checkedRows = (rows || []).filter((r) => checked[r.id]);

  const saveUpdates = async (updates) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/prime/store/products", { updates });
      const failed = data.results.filter((r) => !r.ok);
      const belowFloor = failed.filter((r) => r.below_floor);
      if (belowFloor.length) {
        toast.warning(`${belowFloor.length} product(s) below the profitable floor — tick Authorize on the row and save again to override.`);
      }
      failed.filter((r) => !r.below_floor).forEach((r) => toast.error(r.error));
      if (failed.length < data.results.length) toast.success("PRIME Store updated");
      setDrafts({});
      await load();
    } catch (err) {
      toast.error(apiError(err, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  const saveDirty = () => saveUpdates(dirtyRows.map((r) => {
    const d = draft(r);
    return { product_id: r.id, prime_store: !!d.prime_store, prime_price: d.prime_price === "" ? null : parseFloat(d.prime_price), variant_prices: parseVariantPrices(d), authorize_below_floor: !!d.authorize };
  }));

  const bulkSelect = () => saveUpdates(checkedRows.map((r) => {
    const d = draft(r);
    const price = d.prime_price !== "" ? parseFloat(d.prime_price) : r.prime_price;
    return { product_id: r.id, prime_store: true, prime_price: price ?? null, variant_prices: parseVariantPrices(d), authorize_below_floor: !!d.authorize };
  }));
  const bulkRemove = () => saveUpdates(checkedRows.map((r) => ({ product_id: r.id, prime_store: false })));

  const selCls = "h-9 rounded-lg border border-white/10 bg-black/30 px-2 text-xs text-white focus:border-amber-500/60 focus:outline-none";

  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="prime-store-manager">
      <div className="flex items-center gap-2">
        <Store className="h-4 w-4 text-amber-500" />
        <h2 className="text-sm font-extrabold uppercase tracking-[0.14em] text-neutral-300">PRIME Store products</h2>
      </div>
      <p className="mt-1 text-xs text-neutral-500">Select products and set member prices. PRIME price must be below the regular price; dipping under the margin floor needs explicit authorization. Only selected, active, in-stock products with valid prices appear in the store.</p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / SKU / keyword" className={`${selCls} pl-8 w-56`} data-testid="prime-store-search" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={selCls} data-testid="prime-store-filter-category">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
        </select>
        <select value={brand} onChange={(e) => setBrand(e.target.value)} className={selCls} data-testid="prime-store-filter-brand">
          <option value="">All brands</option>
          {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select value={stockF} onChange={(e) => setStockF(e.target.value)} className={selCls} data-testid="prime-store-filter-stock">
          <option value="">Any stock</option>
          <option value="in_stock">In stock</option>
          <option value="out_of_stock">Out of stock</option>
        </select>
        <select value={primeF} onChange={(e) => setPrimeF(e.target.value)} className={selCls} data-testid="prime-store-filter-prime">
          <option value="">All products</option>
          <option value="selected">In PRIME Store</option>
          <option value="ready">Live in store</option>
          <option value="not_selected">Not selected</option>
        </select>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={bulkSelect} disabled={saving || !checkedRows.length} data-testid="prime-store-bulk-add"
            className="rounded-lg bg-amber-500/15 px-3 py-2 text-xs font-bold text-amber-400 hover:bg-amber-500/25 disabled:opacity-40">
            Add {checkedRows.length || ""} to store
          </button>
          <button onClick={bulkRemove} disabled={saving || !checkedRows.length} data-testid="prime-store-bulk-remove"
            className="rounded-lg bg-red-500/10 px-3 py-2 text-xs font-bold text-red-400 hover:bg-red-500/20 disabled:opacity-40">
            Remove {checkedRows.length || ""}
          </button>
          <button onClick={saveDirty} disabled={saving || !dirtyRows.length} data-testid="prime-store-save"
            className="flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-xs font-bold text-black hover:bg-amber-400 disabled:opacity-40">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save {dirtyRows.length ? `(${dirtyRows.length})` : ""}
          </button>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="prime-store-table">
          <thead>
            <tr className="border-b border-white/10 text-[11px] uppercase tracking-wider text-neutral-500">
              <th className="pb-2 pr-2 w-8"></th>
              <th className="pb-2 pr-4">Product</th>
              <th className="pb-2 pr-4">Stock</th>
              <th className="pb-2 pr-4">Regular</th>
              <th className="pb-2 pr-4">PRIME price</th>
              <th className="pb-2 pr-4">Savings</th>
              <th className="pb-2 pr-4">Margin</th>
              <th className="pb-2">In store</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={8} className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-amber-500" /></td></tr>}
            {rows?.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-xs text-neutral-600" data-testid="prime-store-empty">No products match these filters.</td></tr>}
            {rows?.map((r) => {
              const d = draft(r);
              const pp = d.prime_price === "" ? null : parseFloat(d.prime_price);
              const belowFloor = pp != null && pp > 0 && pp < r.margin_floor;
              const invalid = d.prime_store && pp != null && pp >= r.regular_price;
              return (
                <tr key={r.id} className={`border-b border-white/5 ${isDirty(r) ? "bg-amber-500/[0.04]" : ""}`} data-testid={`prime-store-row-${r.id}`}>
                  <td className="py-2.5 pr-2">
                    <input type="checkbox" checked={!!checked[r.id]} onChange={(e) => setChecked((c) => ({ ...c, [r.id]: e.target.checked }))}
                      className="h-4 w-4 accent-amber-500" data-testid={`prime-store-check-${r.id}`} />
                  </td>
                  <td className="py-2.5 pr-4">
                    <div className="flex items-center gap-2.5">
                      {r.image && <img src={r.image} alt="" className="h-9 w-9 rounded-lg object-cover" />}
                      <div>
                        <p className="font-semibold text-white leading-tight">{r.name}</p>
                        <p className="text-[10px] text-neutral-500">{r.category}{r.total_stock <= 0 && " · OUT OF STOCK"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 pr-4 text-xs text-neutral-400">{r.total_stock.toLocaleString("en-IN")}</td>
                  <td className="py-2.5 pr-4 text-xs text-neutral-300">{fmt(r.regular_price)}<span className="text-neutral-600">/{r.unit}</span></td>
                  <td className="py-2.5 pr-4">
                    <div className="flex items-center gap-1.5">
                      <input type="number" min="0" step="0.01" value={d.prime_price}
                        onChange={(e) => setDraft(r.id, { prime_store: true, prime_price: e.target.value })}
                        placeholder="—"
                        className={`h-8 w-24 rounded-lg border bg-black/30 px-2 text-xs text-white focus:outline-none ${invalid ? "border-red-500/60" : belowFloor ? "border-amber-500/60" : "border-white/10 focus:border-amber-500/60"}`}
                        data-testid={`prime-store-price-${r.id}`} />
                      {(r.variants || []).length > 1 && (
                        <button
                          onClick={() => setVarModal(r)}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 text-neutral-400 transition-colors hover:border-amber-500/50 hover:text-amber-500"
                          data-testid={`prime-store-variant-edit-${r.id}`}
                          aria-label={`Edit per-variant PRIME prices for ${r.name}`}
                          title="Per-variant PRIME prices"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    {invalid && <p className="mt-1 text-[10px] font-semibold text-red-400">Must be below {fmt(r.regular_price)}</p>}
                    {(r.variants || []).some((v) => v.override != null) && (
                      <p className="mt-1 text-[10px] font-semibold text-amber-500" data-testid={`prime-store-has-overrides-${r.id}`}>
                        {(r.variants || []).filter((v) => v.override != null).length} variant override{(r.variants || []).filter((v) => v.override != null).length === 1 ? "" : "s"}
                      </p>
                    )}
                  </td>
                  <td className="py-2.5 pr-4 text-xs font-semibold text-emerald-400">
                    {pp != null && pp > 0 && pp < r.regular_price ? `${fmt(r.regular_price - pp)} (${Math.round((1 - pp / r.regular_price) * 100)}%)` : "—"}
                  </td>
                  <td className="py-2.5 pr-4">
                    {belowFloor ? (
                      <label className="flex items-center gap-1.5 text-[10px] font-bold text-amber-400" data-testid={`prime-store-warn-${r.id}`}>
                        <AlertTriangle className="h-3.5 w-3.5" /> Below floor {fmt(r.margin_floor)}
                        <input type="checkbox" checked={!!d.authorize} onChange={(e) => setDraft(r.id, { authorize: e.target.checked })}
                          className="h-3.5 w-3.5 accent-amber-500" data-testid={`prime-store-authorize-${r.id}`} />
                        <span className="text-neutral-500 font-semibold">authorize</span>
                      </label>
                    ) : (
                      <span className="flex items-center gap-1 text-[10px] text-neutral-600"><ShieldCheck className="h-3.5 w-3.5" /> floor {fmt(r.margin_floor)}</span>
                    )}
                  </td>
                  <td className="py-2.5">
                    <button
                      onClick={() => setDraft(r.id, { prime_store: !d.prime_store })}
                      className={`relative h-6 w-11 rounded-full transition-colors ${d.prime_store ? "bg-amber-500" : "bg-black/10"}`}
                      data-testid={`prime-store-toggle-${r.id}`}
                      aria-label="Toggle PRIME Store"
                      role="switch"
                      aria-checked={!!d.prime_store}
                    >
                      <span className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform motion-reduce:transition-none ${d.prime_store ? "translate-x-5" : "translate-x-0"}`} />
                    </button>
                    {r.prime_store && !r.store_ready && <p className="mt-1 text-[10px] font-semibold text-red-400">Not live — needs valid price & stock</p>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Per-variant PRIME price editor (pencil action) */}
      {varModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setVarModal(null)}>
          <div className="w-full max-w-md rounded-2xl border border-line bg-surface p-5 shadow-lift" onClick={(e) => e.stopPropagation()} data-testid="prime-variant-modal">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                {varModal.image && <img src={varModal.image} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                <div>
                  <h3 className="font-display text-base font-semibold text-1">{varModal.name}</h3>
                  <p className="text-xs text-3">Per-variant PRIME prices · base {draft(varModal).prime_price !== "" ? fmt(parseFloat(draft(varModal).prime_price)) : "not set"}</p>
                </div>
              </div>
              <button onClick={() => setVarModal(null)} className="rounded-full p-1.5 text-3 transition-colors hover:bg-hover hover:text-1" aria-label="Close"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 space-y-2" data-lenis-prevent>
              {(varModal.variants || []).map((vr) => {
                const dv = (draft(varModal).variant_prices || {})[vr.sku] ?? "";
                const dvNum = dv === "" ? null : parseFloat(dv);
                const vInvalid = dvNum != null && dvNum >= vr.price;
                return (
                  <div key={vr.sku} className="flex items-center gap-3 rounded-xl border border-line bg-paper px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-bold text-1">{vr.label || vr.sku}</p>
                      <p className="text-[10px] text-3">regular {fmt(vr.price)}</p>
                    </div>
                    <input type="number" min="0" step="0.01" value={dv}
                      onChange={(e) => { setDraft(varModal.id, { prime_store: true }); setVariantPrice(varModal, vr.sku, e.target.value); }}
                      placeholder={draft(varModal).prime_price !== "" ? String(draft(varModal).prime_price) : "base"}
                      className={`h-8 w-24 rounded-lg border bg-surface px-2 text-xs text-1 focus:outline-none ${vInvalid ? "border-red-500/60" : "border-line focus:border-amber-500/60"}`}
                      data-testid={`prime-store-variant-price-${varModal.id}-${vr.sku}`} />
                    {vInvalid && <span className="text-[10px] font-semibold text-red-400">&lt; {fmt(vr.price)}</span>}
                    {!vInvalid && dvNum != null && vr.prime_price == null && <span className="text-[10px] font-semibold text-amber-500">new</span>}
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-[10px] text-3">Blank uses the base PRIME price · 0 clears an override · each price must stay below that variant's regular price.</p>
            <button onClick={() => setVarModal(null)} className="mt-4 w-full rounded-full bg-[#242521] py-2.5 text-xs font-bold text-[#E4B66D] transition-colors hover:bg-black" data-testid="prime-variant-modal-done">
              Done — review &amp; Save above
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
